import { FormControl, FormHelperText, FormLabel, Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useField } from 'formik';
import React, { useState } from 'react';
import { ButtonStyleCheckboxProps } from './button-style-checkbox.component';
import { useHelperTextClasses } from '../BaseTextField';

const useGroupStyles = makeStyles<ButtonStyleCheckboxGroupProps>()(theme => ({
  label: {
    marginBottom: theme.spacing(2),
  },
  items: {
    display: 'flex',
    flexDirection: 'column',
  },
  noButtonItems: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '10px'
  },
  noButtonLabel: {
    paddingLeft: '8px',
    lineHeight: '20px'
  }
}));

export type ButtonStyleCheckboxGroupProps = {
  name: string;
  children: React.ReactElement<ButtonStyleCheckboxProps>[];
  label?: React.ReactNode;
  color?: 'primary' | 'secondary';
  shouldGroup?: boolean;
  error?: boolean;
  helperText?: string;
  className?: string;
  id?: string;
  noButton?: boolean;
  showError?: boolean;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  classes?: Partial<ReturnType<typeof useGroupStyles>['classes']>;
};

export const ButtonStyleCheckboxGroup: React.FC<ButtonStyleCheckboxGroupProps> = ({ shouldGroup = true, noButton = false, showError = false, ...props }) => {
  const { classes: helperTextClasses } = useHelperTextClasses({});
  const { classes: groupClasses } = useGroupStyles(props, { props });

  const validate = React.useCallback((values) => {
    if (!props.required) {
      return;
    }

    let error;

    if (!values) {
      error = 'Required';
    }

    if (Array.isArray(values) && values.filter(x => !!x).length === 0) {
      error = 'Required';
    }

    return error;
  }, [ props.required ]);

  const [ field, meta, helpers ] = useField({
    name: props.name,
    validate
  });

  const defaultOtherValue = React.useMemo(() => {
    // if there are no children, the result of React.Children.map() is undefined, which blows up the filter call
    const values = React.Children.map(props.children, child => child?.props?.value)?.filter(v => v) ?? [];

    if (Array.isArray(field.value)) {
      const others = (field.value as string[]).filter(v => !values.includes(v));

      return others?.[0] || '';
    }

    return '';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // here can be a bug if you will fetch items from the internet and other=true
  // other field will be preselected by default with initial value
  const [ otherChecked, setOtherChecked ] = useState(defaultOtherValue ? true : false);
  const [ otherText, setOtherText ] = useState(defaultOtherValue);

  const getValue = (childProps: ButtonStyleCheckboxProps) => {
    if (childProps.other) {
      if (!shouldGroup) {
        return (field.value ?? {})['other'];
      } else {
        return otherText;
      }
    }

    return childProps.value;
  };

  const getChecked = (childProps: ButtonStyleCheckboxProps) => {
    if (!shouldGroup) {
      if (childProps.other) {
        return otherChecked;
      } else {
        return !!(field.value || {})[childProps.value];
      }
    }

    if (childProps.other) {
      return otherChecked;
    }

    const fieldVal = field.value || [];

    return fieldVal.includes(childProps.value);
  };

  const handleUngroupedChange = React.useCallback((checked: boolean, value: string, other: boolean) => {

    if (other && checked) {
      if (!otherChecked) {
        setOtherChecked(true);
      }

      helpers.setValue({
        ...field.value,
        'other': value
      });

      setOtherText(value);
    } else if (other && !checked) {
      if (otherChecked) {
        setOtherChecked(false);
      }

      const newValue = { ...field.value };

      newValue['other'] = '';

      helpers.setValue(newValue);

      setOtherText('');
    } else {
      const newValue = { ...(field.value ?? {}) };

      newValue[value] = checked;

      helpers.setValue(newValue);
    }
  }, [ field.value, helpers, otherChecked ]);

  const handleGroupedChange = React.useCallback((checked: boolean, value: string, other: boolean) => {
    if (other && checked) {
      if (!otherChecked) {
        setOtherChecked(true);
      }

      const filtered = field.value?.filter((x: string) => x !== otherText && x !== value) ?? [];
      const newValue = filtered.concat([ value ]);

      helpers.setValue(newValue);

      setOtherText(value);
    } else if (other && !checked) {
      if (otherChecked) {
        setOtherChecked(false);
      }
      helpers.setValue(field.value?.filter((x: string) => x !== otherText && x !== value) ?? []);
    } else if (checked) {
      helpers.setValue((field.value ?? []).concat([ value ]));
    } else if (!checked) {
      helpers.setValue(field.value?.filter((x: string) => x !== value) ?? []);
    }
  }, [ field.value, helpers, otherChecked, otherText ]);


  const children = React.Children.map(props.children, child => {
    if (!React.isValidElement(child)) {
      return;
    }

    return React.cloneElement<ButtonStyleCheckboxProps>(child, {
      name: props.name,
      color: props.color,
      noButton,
      onChange: shouldGroup
        ? handleGroupedChange
        : handleUngroupedChange,
      checked: getChecked(child.props),
      value: getValue(child.props),
      otherText
    });
  });

  const hasError = showError && Boolean(meta.error && meta.touched);
  const helperText = hasError ? meta.error : props.helperText;

  return (
    <FormControl disabled={props.disabled} component="fieldset" error={props.error} className={props.className} id={props.id} color={props.color} fullWidth={props.fullWidth}>
      <FormLabel component="legend" className={noButton ? groupClasses.noButtonLabel : groupClasses.label} error={hasError}>
        {props.label}
      </FormLabel>
      <Box className={noButton ? groupClasses.noButtonItems : groupClasses.items}>
        {children}
      </Box>
      {helperText && (
        <FormHelperText className={helperTextClasses.root} error={hasError}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
